import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ArrowLeft, Calendar, MapPin, Mountain, Users, DollarSign,
  CheckSquare, Footprints, Utensils, Home as HomeIcon, AlertTriangle, Package,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BookingDetail() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [program, setProgram] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/trekker/bookings/${bookingId}`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setBooking(data.booking);
          setProgram(data.program);
          setChecklist(data.booking?.packing_checklist || {});
        }
      } catch (err) {
        console.error("Failed to load booking:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [bookingId]);

  const togglePackingItem = async (itemId) => {
    const updated = { ...checklist, [itemId]: !checklist[itemId] };
    setChecklist(updated);
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/trekker/bookings/${bookingId}/packing`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ packing_checklist: updated }),
      });
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 pt-20 flex justify-center items-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-stone-950 pt-20 flex flex-col justify-center items-center">
        <p className="text-stone-400 text-lg mb-4">Booking not found</p>
        <Link to="/portal">
          <Button variant="outline" className="border-stone-700 text-stone-300 rounded-xl">
            Back to Portal
          </Button>
        </Link>
      </div>
    );
  }

  const packingCategories = {};
  if (program?.packing_list) {
    program.packing_list.forEach((item) => {
      if (!packingCategories[item.category]) {
        packingCategories[item.category] = [];
      }
      packingCategories[item.category].push(item);
    });
  }

  const totalPackingItems = program?.packing_list?.length || 0;
  const packedItems = Object.values(checklist).filter(Boolean).length;
  const packingProgress = totalPackingItems > 0 ? Math.round((packedItems / totalPackingItems) * 100) : 0;

  const difficultyColor = {
    easy: "text-emerald-400",
    moderate: "text-amber-400",
    difficult: "text-orange-400",
    extreme: "text-red-400",
  };

  return (
    <div className="min-h-screen bg-stone-950 pt-20 pb-16" data-testid="booking-detail">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Back button */}
        <Link to="/portal" className="inline-flex items-center text-stone-400 hover:text-amber-400 mb-6 transition-colors" data-testid="back-to-portal">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Portal
        </Link>

        {/* Header */}
        <div className="bg-stone-900/60 border border-stone-800 rounded-2xl p-6 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="booking-title">
                {booking.program_name || program?.name || "Trek Program"}
              </h1>
              <div className="flex flex-wrap gap-4 text-stone-400 text-sm">
                {booking.departure_date && (
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-amber-500" />
                    {booking.departure_date} {booking.return_date ? `- ${booking.return_date}` : ""}
                  </span>
                )}
                {program?.duration_days && (
                  <span className="flex items-center gap-1.5">
                    <Footprints className="w-4 h-4 text-amber-500" />
                    {program.duration_days} days
                  </span>
                )}
                {program?.difficulty && (
                  <span className={`flex items-center gap-1.5 ${difficultyColor[program.difficulty] || "text-stone-400"}`}>
                    <Mountain className="w-4 h-4" />
                    {program.difficulty.charAt(0).toUpperCase() + program.difficulty.slice(1)}
                  </span>
                )}
              </div>
            </div>
            <Badge
              className={`text-sm px-3 py-1 ${
                booking.status === "confirmed"
                  ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                  : booking.status === "completed"
                  ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                  : "bg-amber-500/20 text-amber-400 border-amber-500/30"
              }`}
              data-testid="booking-status"
            >
              {booking.status}
            </Badge>
          </div>

          {booking.total_amount > 0 && (
            <div className="mt-4 flex items-center gap-2 text-amber-400">
              <DollarSign className="w-4 h-4" />
              <span className="text-lg font-semibold">USD {booking.total_amount.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="itinerary" className="space-y-4">
          <TabsList className="bg-stone-900/80 border border-stone-800 rounded-xl p-1 w-full sm:w-auto">
            <TabsTrigger
              value="itinerary"
              className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400"
              data-testid="tab-itinerary"
            >
              <MapPin className="w-4 h-4 mr-1.5" />
              Itinerary
            </TabsTrigger>
            <TabsTrigger
              value="packing"
              className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400"
              data-testid="tab-packing"
            >
              <CheckSquare className="w-4 h-4 mr-1.5" />
              Packing
              {totalPackingItems > 0 && (
                <span className="ml-1.5 text-xs bg-stone-700 text-stone-300 px-1.5 py-0.5 rounded-full">
                  {packedItems}/{totalPackingItems}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="participants"
              className="rounded-lg data-[state=active]:bg-amber-600 data-[state=active]:text-white text-stone-400"
              data-testid="tab-participants"
            >
              <Users className="w-4 h-4 mr-1.5" />
              Team
            </TabsTrigger>
          </TabsList>

          {/* Itinerary Tab */}
          <TabsContent value="itinerary">
            <div className="space-y-4" data-testid="itinerary-section">
              {program?.itinerary?.length > 0 ? (
                program.itinerary.map((day, idx) => (
                  <div
                    key={idx}
                    className="bg-stone-900/60 border border-stone-800 rounded-xl p-5 relative overflow-hidden"
                    data-testid={`itinerary-day-${day.day}`}
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 to-amber-700" />
                    <div className="pl-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="bg-amber-600/20 text-amber-400 text-xs font-semibold px-2.5 py-1 rounded-lg">
                              Day {day.day}
                            </span>
                            <h3 className="text-lg font-semibold text-white">{day.title}</h3>
                          </div>
                          <p className="text-stone-400 text-sm leading-relaxed mb-3">
                            {day.description}
                          </p>
                          <div className="flex flex-wrap gap-3 text-xs text-stone-500">
                            {day.altitude && (
                              <span className="flex items-center gap-1 bg-stone-800 px-2.5 py-1 rounded-lg">
                                <Mountain className="w-3 h-3 text-amber-500" />
                                {day.altitude}
                              </span>
                            )}
                            {day.distance && (
                              <span className="flex items-center gap-1 bg-stone-800 px-2.5 py-1 rounded-lg">
                                <Footprints className="w-3 h-3 text-amber-500" />
                                {day.distance}
                              </span>
                            )}
                            {day.meals && (
                              <span className="flex items-center gap-1 bg-stone-800 px-2.5 py-1 rounded-lg">
                                <Utensils className="w-3 h-3 text-amber-500" />
                                {day.meals}
                              </span>
                            )}
                            {day.accommodation && (
                              <span className="flex items-center gap-1 bg-stone-800 px-2.5 py-1 rounded-lg">
                                <HomeIcon className="w-3 h-3 text-amber-500" />
                                {day.accommodation}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-stone-900/60 border border-stone-800 rounded-xl p-8 text-center">
                  <MapPin className="w-10 h-10 text-stone-600 mx-auto mb-3" />
                  <p className="text-stone-400">Detailed itinerary will be available soon</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Packing Tab */}
          <TabsContent value="packing">
            <div data-testid="packing-section">
              {/* Progress bar */}
              {totalPackingItems > 0 && (
                <div className="bg-stone-900/60 border border-stone-800 rounded-xl p-5 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">Packing Progress</span>
                    <span className="text-amber-400 font-semibold" data-testid="packing-progress">
                      {packingProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-stone-800 rounded-full h-2.5">
                    <div
                      className="bg-gradient-to-r from-amber-500 to-amber-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${packingProgress}%` }}
                    />
                  </div>
                  <p className="text-stone-500 text-sm mt-2">
                    {packedItems} of {totalPackingItems} items packed
                    {saving && <span className="text-amber-400 ml-2">Saving...</span>}
                  </p>
                </div>
              )}

              {Object.keys(packingCategories).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(packingCategories).map(([category, items]) => (
                    <div key={category} className="bg-stone-900/60 border border-stone-800 rounded-xl p-5">
                      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4 text-amber-500" />
                        {category}
                        <span className="text-stone-500 text-sm font-normal">
                          ({items.filter(i => checklist[i.item_id]).length}/{items.length})
                        </span>
                      </h3>
                      <div className="space-y-2.5">
                        {items.map((item) => (
                          <label
                            key={item.item_id}
                            className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all ${
                              checklist[item.item_id]
                                ? "bg-emerald-500/10 border border-emerald-500/20"
                                : "hover:bg-stone-800/50"
                            }`}
                            data-testid={`packing-item-${item.item_id}`}
                          >
                            <Checkbox
                              checked={!!checklist[item.item_id]}
                              onCheckedChange={() => togglePackingItem(item.item_id)}
                              className="border-stone-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                            />
                            <span className={`text-sm ${
                              checklist[item.item_id] ? "text-stone-500 line-through" : "text-stone-200"
                            }`}>
                              {item.name}
                            </span>
                            {item.is_essential && (
                              <span className="text-xs text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded ml-auto">
                                Essential
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-stone-900/60 border border-stone-800 rounded-xl p-8 text-center">
                  <CheckSquare className="w-10 h-10 text-stone-600 mx-auto mb-3" />
                  <p className="text-stone-400">Packing list will be available soon</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Participants Tab */}
          <TabsContent value="participants">
            <div data-testid="participants-section">
              {booking.participants?.length > 0 ? (
                <div className="space-y-3">
                  {booking.participants.map((p, idx) => (
                    <div key={p.participant_id || idx} className="bg-stone-900/60 border border-stone-800 rounded-xl p-5" data-testid={`participant-${idx}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-amber-600/20 flex items-center justify-center">
                          <span className="text-amber-400 font-semibold">{p.name?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <div>
                          <h4 className="text-white font-medium">{p.name}</h4>
                          {p.age > 0 && <p className="text-stone-500 text-sm">{p.age} years old{p.gender ? ` - ${p.gender}` : ""}</p>}
                        </div>
                      </div>
                      {p.emergency_contact && (
                        <p className="text-stone-400 text-sm mt-2">
                          <span className="text-stone-500">Emergency Contact:</span> {p.emergency_contact}
                        </p>
                      )}
                      {p.medical_notes && (
                        <div className="mt-2 flex items-start gap-2 text-sm text-amber-400 bg-amber-500/10 rounded-lg p-2.5">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {p.medical_notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-stone-900/60 border border-stone-800 rounded-xl p-8 text-center">
                  <Users className="w-10 h-10 text-stone-600 mx-auto mb-3" />
                  <p className="text-stone-400">Participant details will be updated by the admin</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Notes */}
        {booking.notes && (
          <div className="mt-6 bg-stone-900/60 border border-stone-800 rounded-xl p-5">
            <h3 className="text-white font-semibold mb-2">Notes</h3>
            <p className="text-stone-400 text-sm whitespace-pre-wrap">{booking.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
